import { useCallback, useMemo } from "react";
import { NetworkStatus } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { connectionRows, IssuesDocument, type IssueRow } from "#app/lib/data";
import type { IssueTableQuery } from "./issue-table";

const PAGE_SIZE = 25;

/** The most rows an auto-paging consumer pulls before it stops asking. A
 *  body that needs every row to band or rank them pages on as each page lands,
 *  and past this many the population is a report, not a working list. */
export const AUTO_PAGE_ROW_CAP = 1000;

/**
 * The rows behind a list-shaped issue card (`IssueList`): the same folded
 * `where` + `orderBy` the table pages over, read through the shared `Issues`
 * document and **lazily paged** — the first page loads eagerly and `loadMore`
 * appends the next `skip`-offset page as the viewer scrolls, until the server
 * `total` is exhausted. The whole scoped set is reachable; nothing is a capped
 * chunk. A list never hand-builds its `where`: it takes the query the card's
 * data layer (`useEntityList`) already folded, so the list and the table twin
 * can never disagree about which projects are in the card.
 *
 * Append, not window: the table replaces its window per virtualizer page
 * (`useGroupQuery`), which suits a fixed-height virtualized grid. A stack of
 * rows in a scrolling body has no virtualizer, so it accumulates instead. The
 * `issues` field has no cache merge fn (the windowed tables need replacement
 * semantics), so the accumulation lives in this query's local view — the same
 * shape `useActivityList` uses for the feed.
 */
export function useIssueRowsPaged(
  query: IssueTableQuery,
  options?: {
    /** Rows per page. The default suits a scrolling body that appends as the
     *  reader reaches the tail; a list that must hold EVERY row in memory to
     *  read a fact off each one (the personal follow up queue derives its
     *  tiers client-side) asks for a wider page. */
    pageSize?: number;
  },
): {
  rows: IssueRow[];
  loading: boolean;
  /** A further page is in flight — show a tail skeleton, don't re-fire. */
  loadingMore: boolean;
  /** More rows exist past the loaded window. */
  hasMore: boolean;
  /** Append the next page. No-op while loading or exhausted. */
  loadMore: () => void;
} {
  const pageSize = options?.pageSize ?? PAGE_SIZE;
  const variables = useMemo(
    () => ({ where: query.where, orderBy: query.orderBy, limit: pageSize }),
    [query.where, query.orderBy, pageSize],
  );
  const { data, loading, fetchMore, networkStatus } = useQuery(IssuesDocument, {
    variables,
    notifyOnNetworkStatusChange: true,
  });
  const rows = useMemo(() => connectionRows(data?.issues), [data]);

  const total = data?.issues?.total ?? 0;
  const loadingMore = networkStatus === NetworkStatus.fetchMore;
  const hasMore = rows.length < total;

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    void fetchMore({
      variables: { skip: rows.length },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.issues) return prev;
        if (!prev?.issues) return fetchMoreResult;
        return {
          ...prev,
          issues: {
            ...fetchMoreResult.issues,
            edges: [...prev.issues.edges, ...fetchMoreResult.issues.edges],
          },
        };
      },
    });
  }, [fetchMore, loading, loadingMore, hasMore, rows.length]);

  return { rows, loading: loading && !data, loadingMore, hasMore, loadMore };
}
